import { JobStatus } from '@/api/generatedApi';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { getApiError } from '@/types/apiError';
import { useQuery } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

export const AUTO_LABEL_LIBRARY_JOB_QUERY_KEY = 'taxonomy.autoLabelLibraryJob';

const POLL_INTERVAL_MS = 1500;
const TERMINAL_STATUSES = new Set<JobStatus>([JobStatus.COMPLETED, JobStatus.FAILED, JobStatus.CANCELLED]);

const isTerminal = (status: JobStatus) => TERMINAL_STATUSES.has(status);

const isNotFound = (error: unknown): boolean =>
  !!error && typeof error === 'object' && (error as AxiosError).response?.status === 404;

export const useAutoLabelLibraryJob = (taxonomyId: string | undefined, jobId: string | undefined) => {
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();
  const isEnabled = Boolean(taxonomyId) && Boolean(jobId);

  const { data, error, isLoading } = useQuery({
    queryKey: [AUTO_LABEL_LIBRARY_JOB_QUERY_KEY, { tenantId, taxonomyId, jobId }],
    queryFn: () => api.taxonomy.getAutoLabelLibraryJob(tenantId, taxonomyId ?? '', jobId ?? ''),
    enabled: isEnabled,
    retry: (failureCount, error_) => !isNotFound(error_) && failureCount < 3,
    refetchInterval: query => {
      if (isNotFound(query.state.error)) return false;
      const status = query.state.data?.data.job.status;
      return status && isTerminal(status) ? false : POLL_INTERVAL_MS;
    },
  });

  const job = data?.data.job;
  const notFound = isNotFound(error);
  return {
    job,
    isPolling: isEnabled && !notFound && (!job || !isTerminal(job.status)),
    isLoading,
    jobError: getApiError(error),
    notFound,
  };
};
